/**
 * Adds a parsed `query` object to a router location, mirroring the behavior that
 * `connected-react-router` used to provide (its reducer injected `location.query`).
 * `redux-first-history` does not, so keep `state.router.location.query` available
 * to existing consumers.
 *
 * @param {Record<string, any> | null | undefined} location
 * @returns {Record<string, any> | null | undefined}
 */
const parseSearch = (searchQuery) => {
  const query = {};

  new URLSearchParams(searchQuery).forEach((value, key) => {
    const existingValue = query[key];

    if (typeof existingValue === 'undefined') {
      query[key] = value;
    } else if (Array.isArray(existingValue)) {
      existingValue.push(value);
    } else {
      query[key] = [existingValue, value];
    }
  });

  return query;
};

export const injectQuery = (location) => {
  if (location && location.query) {
    // Don't inject query if it already exists
    return location;
  }

  const searchQuery = location && location.search;

  if (typeof searchQuery !== 'string' || searchQuery.length === 0) {
    return location ? { ...location, query: {} } : location;
  }

  return { ...location, query: parseSearch(searchQuery) };
};

/**
 * Wraps `redux-first-history`'s router reducer so the stored location always carries
 * a parsed `query` object, preserving the shape consumers relied on under
 * `connected-react-router`.
 *
 * @param {(state: any, action: any) => any} reducer
 */
export const withRouterQuery = (reducer) => (state, action) => {
  const next = reducer(state, action);
  return next && next.location && !next.location.query ? { ...next, location: injectQuery(next.location) } : next;
};
